import Layout from '../../../layout/default';
import PageTabs from '../componets/PageTabs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getFacilities,
  getFormsList,
  getFolderFormIds,
  getReport,
  getUserGroups,
  getUsers,
  getFacilitiesReport,
} from '../../../services/reports/statistics';
import { useAuth } from '../../../container/authContext';
import { isResponseOk } from '../../../utility/isResponseOk';
import { MultiSelect } from '../../../components/other/MultiSelect';
import { useDebounce } from 'use-debounce';
import DatePicker from 'react-datepicker';
import { DATE_PICKER_DATE_FORMAT } from '../../../constants';
import FormsModal from './components/FormsModal';
import Select from 'react-select';
import './styles.scss';
import DateRangeCalendar from '../../../components/calendars/DateCalendar/DateCalendar';

const YEARS_RANGE = 50;
const START_YEAR = 2000;

const REPORT_TYPE_EMPLOYEES = 'employees';
const REPORT_TYPE_FACILITIES = 'facilities';

const months = Array.from({ length: 12 }).map((_, i) => new Date(0, i).toLocaleString('en', { month: 'long' }));

const years = Array.from({ length: YEARS_RANGE }).map((_, i) => START_YEAR + i);

const StatisticsPage = () => {
  const { user } = useAuth();
  const companyId = user.company?.id;

  const [reportType, setReportType] = useState(REPORT_TYPE_EMPLOYEES);

  // Employee report state
  const [facilities, setFacilities] = useState(null);
  const [userGroups, setUerGroups] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState(null);
  const [selectedUerGroups, setSelectedUerGroups] = useState(null);
  const [initiationDateAfter, setInitiationDateAfter] = useState(null);
  const [initiationDateBefore, setInitiationDateBefore] = useState(null);
  const [isOpenSelectFormsModal, setIsOpenSelectFormsModal] = useState(false);
  const [selectedUserStatus, setSelectedUserStatus] = useState({ label: 'Active', value: 'active' });
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedForms, setSelectedForms] = useState([]);
  const [selectedFormsData, setSelectedFormsData] = useState([]);
  // The tasks picked next to the forms, as { id, name }.
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [debouncedSelectedFacilities] = useDebounce(selectedFacilities, 500);

  // Facility report state
  const [facilityReportSelectedFacilities, setFacilityReportSelectedFacilities] = useState(null);
  const [facilityReportDateAfter, setFacilityReportDateAfter] = useState(null);
  const [facilityReportDateBefore, setFacilityReportDateBefore] = useState(null);
  const [facilityReportSelectedForms, setFacilityReportSelectedForms] = useState([]);
  const [facilityReportSelectedFormsData, setFacilityReportSelectedFormsData] = useState([]);
  const [facilityReportSelectedTasks, setFacilityReportSelectedTasks] = useState([]);
  const [isFacilityReportLoading, setIsFacilityReportLoading] = useState(false);
  const [isOpenFacilityFormsModal, setIsOpenFacilityFormsModal] = useState(false);

  useEffect(() => {
    (async () => {
      if (companyId) {
        const facilitiesResponse = await getFacilities({ companyId });
        if (isResponseOk(facilitiesResponse)) {
          setFacilities(facilitiesResponse.data);
        }
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (selectedFacilities?.length) {
      const params = {
        company_id: companyId,
        facilities_ids: selectedFacilities.map(({ value }) => value),
      };
      if (selectedMonth) {
        params.hire_month = selectedMonth.value;
      }
      if (selectedUserStatus) {
        params.user_status = selectedUserStatus.value;
      }
      if (selectedUerGroups?.length) {
        params.user_groups_ids = selectedUerGroups.map(({ value }) => value);
      }
      getUsers(params).then((response) => {
        if (isResponseOk(response)) {
          setUserOptions(response.data.map((user) => ({ value: user.id, label: user.full_name })));
          setSelectedUsers((prev) => prev.filter((selectedUser) => response.data.some((user) => user.id === selectedUser.value)));
        }
      });
    } else {
      setUserOptions([]);
      setSelectedUsers([]);
    }
  }, [selectedUserStatus, selectedMonth, selectedFacilities, selectedUerGroups]);

  useEffect(() => {
    setSelectedUerGroups(null);

    (async () => {
      if (debouncedSelectedFacilities?.length) {
        const facilitiesIDs = debouncedSelectedFacilities.map(({ value }) => value);
        const response = await getUserGroups({ companyId, facilitiesIDs });

        if (isResponseOk(response)) {
          setUerGroups(response.data);
        } else {
          setUerGroups(null);
        }
      } else {
        setUerGroups(null);
      }
    })();
  }, [companyId, debouncedSelectedFacilities]);

  const selectedFormsInputValue = useMemo(() => {
    return [...selectedTasks, ...selectedFormsData].map(({ name }) => name).join(', ');
  }, [selectedFormsData, selectedTasks]);

  const facilityReportSelectedFormsInputValue = useMemo(() => {
    return [...facilityReportSelectedTasks, ...facilityReportSelectedFormsData].map(({ name }) => name).join(', ');
  }, [facilityReportSelectedFormsData, facilityReportSelectedTasks]);

  const clearSelectedForms = () => {
    setSelectedForms([]);
    setSelectedFormsData([]);
    setSelectedTasks([]);
  };

  const clearFacilityReportSelectedForms = () => {
    setFacilityReportSelectedForms([]);
    setFacilityReportSelectedFormsData([]);
    setFacilityReportSelectedTasks([]);
  };

  const facilitiesOptions = useMemo(() => {
    return (
      facilities?.map(({ id, name }) => {
        return { value: id, label: name };
      }) ?? []
    );
  }, [facilities]);

  const userGroupsOptions = useMemo(() => {
    return (
      userGroups?.map(({ id, name }) => {
        return { value: id, label: name };
      }) ?? []
    );
  }, [userGroups]);

  const handleFormInitiationPeriod = (date) => {
    const [startDate, endDate] = date;
    setInitiationDateAfter(startDate);
    setInitiationDateBefore(endDate);
    clearSelectedForms();
  };

  const handleOpenSelectFormsModal = () => setIsOpenSelectFormsModal(true);

  const handleCloseSelectFormsModal = () => setIsOpenSelectFormsModal(false);

  const handleSelectForms = (formsIDs, formsData, tasksData) => {
    setSelectedForms(formsIDs);
    setSelectedFormsData(formsData);
    setSelectedTasks(tasksData);
    handleCloseSelectFormsModal();
  };

  const fetchEmployeeForms = useCallback(
    ({ folderId, search, tree } = {}) => {
      const facilitiesIDs = debouncedSelectedFacilities?.map(({ value }) => value);
      const userGroupsIDs = selectedUerGroups?.map(({ value }) => value);
      const usersIds = selectedUsers?.map(({ value }) => value);

      return getFormsList({
        companyId,
        initiationDateBefore,
        facilitiesIDs,
        initiationDateAfter,
        userGroupsIDs,
        usersIds,
        folderId,
        search,
        tree,
      });
    },
    [companyId, debouncedSelectedFacilities, selectedUerGroups, selectedUsers, initiationDateAfter, initiationDateBefore],
  );

  const fetchEmployeeFolderFormIds = useCallback(
    (folderId, tree) => {
      const facilitiesIDs = debouncedSelectedFacilities?.map(({ value }) => value);
      const userGroupsIDs = selectedUerGroups?.map(({ value }) => value);
      const usersIds = selectedUsers?.map(({ value }) => value);

      return getFolderFormIds({
        companyId,
        folderId,
        tree,
        facilitiesIDs,
        initiationDateAfter,
        initiationDateBefore,
        userGroupsIDs,
        usersIds,
      });
    },
    [companyId, debouncedSelectedFacilities, selectedUerGroups, selectedUsers, initiationDateAfter, initiationDateBefore],
  );

  const handleSelectFacilities = (facilities) => {
    setSelectedFacilities(facilities);
    setSelectedUerGroups(null);
    clearSelectedForms();
  };

  const handleSelectUserGroups = (userGroups) => {
    setSelectedUerGroups(userGroups);
    clearSelectedForms();
  };

  const generateReport = async () => {
    const facilitiesIDs = debouncedSelectedFacilities?.map(({ value }) => value);
    const userGroupsIDs = selectedUerGroups?.map(({ value }) => value);
    const userStatus = selectedUserStatus.value;
    const selectedMonthValue = selectedMonth?.value;
    // "Individual users" only lists users outside the selected user groups, so the
    // two selections are additive on the backend: selected groups + these extras.
    // Sending nothing here means "just the selected groups" (or, with no groups
    // selected either, every user in the selected facilities).
    const usersIds = selectedUsers?.map(({ value }) => value) ?? [];

    try {
      setIsGeneratingReport(true);
      const response = await getReport({
        companyId,
        formsIDs: selectedForms,
        tasksIds: selectedTasks.map(({ id }) => id),
        initiationDateAfter,
        initiationDateBefore,
        userGroupsIDs,
        facilitiesIDs,
        userStatus,
        selectedMonthValue,
        usersIds,
      });
      if (isResponseOk(response)) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Statistics.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
      setIsGeneratingReport(false);
    } catch (e) {
      console.log(e);
      setIsGeneratingReport(false);
    }
  };

  // Facility report handlers
  const handleFacilityReportDateChange = (date) => {
    const [startDate, endDate] = date;
    setFacilityReportDateAfter(startDate);
    setFacilityReportDateBefore(endDate);
    clearFacilityReportSelectedForms();
  };

  const fetchFacilityForms = useCallback(
    ({ folderId, search, tree } = {}) => {
      const facilitiesIDs = facilityReportSelectedFacilities?.map(({ value }) => value);

      return getFormsList({
        companyId,
        facilitiesIDs,
        initiationDateAfter: facilityReportDateAfter,
        initiationDateBefore: facilityReportDateBefore,
        folderId,
        search,
        tree,
      });
    },
    [companyId, facilityReportSelectedFacilities, facilityReportDateAfter, facilityReportDateBefore],
  );

  const fetchFacilityFolderFormIds = useCallback(
    (folderId, tree) => {
      const facilitiesIDs = facilityReportSelectedFacilities?.map(({ value }) => value);

      return getFolderFormIds({
        companyId,
        folderId,
        tree,
        facilitiesIDs,
        initiationDateAfter: facilityReportDateAfter,
        initiationDateBefore: facilityReportDateBefore,
      });
    },
    [companyId, facilityReportSelectedFacilities, facilityReportDateAfter, facilityReportDateBefore],
  );

  const handleOpenFacilityFormsModal = () => setIsOpenFacilityFormsModal(true);
  const handleCloseFacilityFormsModal = () => setIsOpenFacilityFormsModal(false);

  const handleSelectFacilityForms = (formsIDs, formsData, tasksData) => {
    setFacilityReportSelectedForms(formsIDs);
    setFacilityReportSelectedFormsData(formsData);
    setFacilityReportSelectedTasks(tasksData);
    handleCloseFacilityFormsModal();
  };

  const generateFacilityReport = async () => {
    const facilitiesIDs = facilityReportSelectedFacilities?.map(({ value }) => value);

    try {
      setIsFacilityReportLoading(true);
      const response = await getFacilitiesReport({
        companyId,
        formsIDs: facilityReportSelectedForms,
        tasksIds: facilityReportSelectedTasks.map(({ id }) => id),
        facilitiesIDs,
        initiationDateAfter: facilityReportDateAfter,
        initiationDateBefore: facilityReportDateBefore,
      });
      if (isResponseOk(response)) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'facilities_statistics.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
      setIsFacilityReportLoading(false);
    } catch (e) {
      console.log(e);
      setIsFacilityReportLoading(false);
    }
  };

  const renderReportTypeSelector = () => (
    <div className="form-item mb-2 statistics-report-type">
      <label className="form-label mb-2">Choose the type</label>
      <div className="statistics-radio-group">
        <label className="statistics-radio-label">
          <input
            type="radio"
            name="reportType"
            value={REPORT_TYPE_EMPLOYEES}
            checked={reportType === REPORT_TYPE_EMPLOYEES}
            onChange={() => setReportType(REPORT_TYPE_EMPLOYEES)}
          />
          <span>For employees</span>
        </label>
        <label className="statistics-radio-label">
          <input
            type="radio"
            name="reportType"
            value={REPORT_TYPE_FACILITIES}
            checked={reportType === REPORT_TYPE_FACILITIES}
            onChange={() => setReportType(REPORT_TYPE_FACILITIES)}
          />
          <span>For facilities</span>
        </label>
      </div>
    </div>
  );

  const renderEmployeesAside = () => {
    return (
      <>
        <div className={'form-item mb-2'}>
          <label className={'form-label'}>Facility</label>
          <MultiSelect
            isDisabled={!facilities?.length}
            options={facilitiesOptions}
            value={selectedFacilities}
            placeholder="Select facility"
            allName="All Facilities"
            onChange={handleSelectFacilities}
          />
        </div>
        <div className={'form-item mb-2'}>
          <label className={'form-label'}>User groups</label>
          <MultiSelect
            isDisabled={!userGroups?.length}
            options={userGroupsOptions}
            value={selectedUerGroups}
            placeholder="Select User groups"
            allName="All User Groups"
            onChange={handleSelectUserGroups}
          />
        </div>
        <div className="form-item mb-2">
          <label className="form-label">Hire date</label>
          <Select
            options={[
              { value: 1, label: 'January' },
              { value: 2, label: 'February' },
              { value: 3, label: 'March' },
              { value: 4, label: 'April' },
              { value: 5, label: 'May' },
              { value: 6, label: 'June' },
              { value: 7, label: 'July' },
              { value: 8, label: 'August' },
              { value: 9, label: 'September' },
              { value: 10, label: 'October' },
              { value: 11, label: 'November' },
              { value: 12, label: 'December' },
            ]}
            isClearable
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e)}
            className="form-control-select"
            classNamePrefix="react-select"
          />
        </div>
        <div className="form-item mb-2">
          <label className="form-label">User status</label>
          <Select
            options={[
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={selectedUserStatus}
            onChange={(e) => setSelectedUserStatus(e)}
            className="form-control-select"
            classNamePrefix="react-select"
          />
        </div>
        <div className={'form-item mb-2 select-individual-users-wrapper'}>
          <label className={'form-label'}>Individual users</label>
          <MultiSelect
            isDisabled={!userOptions?.length}
            options={userOptions}
            className="form-control-select"
            classNamePrefix="react-select-multi"
            value={selectedUsers}
            placeholder="Select "
            onChange={(e) => setSelectedUsers(e)}
          />
        </div>
        <div className={'form-item mb-2'}>
          <label className={'form-label'}>Period of submission</label>
          <DateRangeCalendar
            selectedDate={initiationDateAfter}
            startDate={initiationDateAfter}
            endDate={initiationDateBefore}
            handleChange={handleFormInitiationPeriod}
            selectsRange
          />
        </div>
        <div className={'form-item mb-2'}>
          <label className={'form-label'}>Select forms and tasks</label>
          <div className={'form-item-wrapper'}>
            <input
              value={selectedFormsInputValue}
              onClick={handleOpenSelectFormsModal}
              readOnly
              type="text"
              className={'form-control'}
              placeholder={'Select...'}
            />
            <svg className={'icon-search'} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 11C4 7.13401 7.13401 4 11 4C14.866 4 18 7.13401 18 11C18 12.8858 17.2543 14.5974 16.0417 15.8561C16.0073 15.8825 15.9743 15.9114 15.9428 15.9429C15.9113 15.9744 15.8824 16.0074 15.856 16.0418C14.5973 17.2543 12.8857 18 11 18C7.13401 18 4 14.866 4 11ZM16.6176 18.0319C15.078 19.2635 13.125 20 11 20C6.02944 20 2 15.9706 2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11C20 13.125 19.2635 15.0781 18.0319 16.6177L21.707 20.2929C22.0975 20.6834 22.0975 21.3166 21.707 21.7071C21.3165 22.0976 20.6833 22.0976 20.2928 21.7071L16.6176 18.0319Z"
                fill="#98A2B3"
              />
            </svg>
          </div>
        </div>
        <button
          disabled={(!selectedForms.length && !selectedTasks.length) || isGeneratingReport}
          onClick={generateReport}
          className={'btn'}
        >
          <svg className={'mr-2'} xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
            <g clipPath="url(#clip0_5076_1432)">
              <path
                d="M19.6668 3.33136V8.33136M19.6668 8.33136H14.6668M19.6668 8.33136L15.8002 4.69803C14.9045 3.80196 13.7965 3.14737 12.5795 2.79534C11.3625 2.44331 10.0761 2.40532 8.8404 2.68491C7.60472 2.9645 6.46 3.55256 5.51305 4.39421C4.56611 5.23586 3.8478 6.30368 3.42516 7.49803M1.3335 16.6647V11.6647M1.3335 11.6647H6.3335M1.3335 11.6647L5.20016 15.298C6.09579 16.1941 7.20381 16.8487 8.42084 17.2007C9.63787 17.5527 10.9242 17.5907 12.1599 17.3111C13.3956 17.0316 14.5403 16.4435 15.4873 15.6018C16.4342 14.7602 17.1525 13.6924 17.5752 12.498"
                stroke="white"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_5076_1432">
                <rect width="20" height="20" fill="white" transform="translate(0.5)" />
              </clipPath>
            </defs>
          </svg>
          {isGeneratingReport ? 'Generating...' : 'Generate Report'}
        </button>
      </>
    );
  };

  const renderFacilitiesAside = () => {
    return (
      <>
        <div className={'form-item mb-2'}>
          <label className={'form-label'}>Facilities</label>
          <MultiSelect
            isDisabled={!facilities?.length}
            options={facilitiesOptions}
            value={facilityReportSelectedFacilities}
            placeholder="Select "
            allName="All Facilities"
            onChange={(val) => {
              setFacilityReportSelectedFacilities(val);
              clearFacilityReportSelectedForms();
            }}
          />
        </div>
        <div className={'form-item mb-2'}>
          <label className={'form-label'}>Period of submission</label>
          <DateRangeCalendar
            selectedDate={facilityReportDateAfter}
            startDate={facilityReportDateAfter}
            endDate={facilityReportDateBefore}
            handleChange={handleFacilityReportDateChange}
            selectsRange
          />
        </div>
        <div className={'form-item mb-2'}>
          <label className={'form-label'}>Select forms and tasks</label>
          <div className={'form-item-wrapper'}>
            <input
              value={facilityReportSelectedFormsInputValue}
              onClick={handleOpenFacilityFormsModal}
              readOnly
              type="text"
              className={'form-control'}
              placeholder={'Select...'}
            />
            <svg className={'icon-search'} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 11C4 7.13401 7.13401 4 11 4C14.866 4 18 7.13401 18 11C18 12.8858 17.2543 14.5974 16.0417 15.8561C16.0073 15.8825 15.9743 15.9114 15.9428 15.9429C15.9113 15.9744 15.8824 16.0074 15.856 16.0418C14.5973 17.2543 12.8857 18 11 18C7.13401 18 4 14.866 4 11ZM16.6176 18.0319C15.078 19.2635 13.125 20 11 20C6.02944 20 2 15.9706 2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11C20 13.125 19.2635 15.0781 18.0319 16.6177L21.707 20.2929C22.0975 20.6834 22.0975 21.3166 21.707 21.7071C21.3165 22.0976 20.6833 22.0976 20.2928 21.7071L16.6176 18.0319Z"
                fill="#98A2B3"
              />
            </svg>
          </div>
        </div>
        <button
          disabled={(!facilityReportSelectedForms.length && !facilityReportSelectedTasks.length) || isFacilityReportLoading}
          onClick={generateFacilityReport}
          className={'btn'}
        >
          <svg className={'mr-2'} xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
            <g clipPath="url(#clip0_5076_1432)">
              <path
                d="M19.6668 3.33136V8.33136M19.6668 8.33136H14.6668M19.6668 8.33136L15.8002 4.69803C14.9045 3.80196 13.7965 3.14737 12.5795 2.79534C11.3625 2.44331 10.0761 2.40532 8.8404 2.68491C7.60472 2.9645 6.46 3.55256 5.51305 4.39421C4.56611 5.23586 3.8478 6.30368 3.42516 7.49803M1.3335 16.6647V11.6647M1.3335 11.6647H6.3335M1.3335 11.6647L5.20016 15.298C6.09579 16.1941 7.20381 16.8487 8.42084 17.2007C9.63787 17.5527 10.9242 17.5907 12.1599 17.3111C13.3956 17.0316 14.5403 16.4435 15.4873 15.6018C16.4342 14.7602 17.1525 13.6924 17.5752 12.498"
                stroke="white"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_5076_1432">
                <rect width="20" height="20" fill="white" transform="translate(0.5)" />
              </clipPath>
            </defs>
          </svg>
          {isFacilityReportLoading ? 'Generating...' : 'Generate Report'}
        </button>
      </>
    );
  };

  const renderAside = () => {
    return (
      <aside className={'report-builder-aside'}>
        <h2 className={'title d-flex justify-space-between'}>Statistics</h2>
        {renderReportTypeSelector()}
        {reportType === REPORT_TYPE_EMPLOYEES ? renderEmployeesAside() : renderFacilitiesAside()}
      </aside>
    );
  };

  const renderContent = () => {
    return (
      <div className={'report-builder-content'}>
        <div className={'report-builder-empty'}>
          <h3>Download Excel Statistics File</h3>
          <p>
            {reportType === REPORT_TYPE_EMPLOYEES
              ? "To Download a Statistic report, please make sure to specify all the required parameters on the right-hand panel and click the 'Generate Report' button"
              : "To download a Facility Statistics report, select a facility, period of submission, and forms or tasks on the right-hand panel, then click 'Generate Report'"}
          </p>
        </div>
      </div>
    );
  };

  const currentFormsModal = reportType === REPORT_TYPE_EMPLOYEES ? isOpenSelectFormsModal : isOpenFacilityFormsModal;
  const currentSelectedForms = reportType === REPORT_TYPE_EMPLOYEES ? selectedForms : facilityReportSelectedForms;
  const currentSelectedFormsData = reportType === REPORT_TYPE_EMPLOYEES ? selectedFormsData : facilityReportSelectedFormsData;
  const currentSelectedTasks = reportType === REPORT_TYPE_EMPLOYEES ? selectedTasks : facilityReportSelectedTasks;
  const currentOnClose = reportType === REPORT_TYPE_EMPLOYEES ? handleCloseSelectFormsModal : handleCloseFacilityFormsModal;
  const currentOnConfirm = reportType === REPORT_TYPE_EMPLOYEES ? handleSelectForms : handleSelectFacilityForms;
  const currentFetchForms = reportType === REPORT_TYPE_EMPLOYEES ? fetchEmployeeForms : fetchFacilityForms;
  const currentFetchFolderFormIds = reportType === REPORT_TYPE_EMPLOYEES ? fetchEmployeeFolderFormIds : fetchFacilityFolderFormIds;

  return (
    <Layout title={'Statistics'}>
      <div style={{ width: '100%' }}>
        <PageTabs />
        <div className={'report-builder'} style={{ height: 'calc(100% - 56px)' }}>
          {renderContent()}
          {renderAside()}
        </div>
      </div>
      {currentFormsModal && (
        <FormsModal
          fetchForms={currentFetchForms}
          fetchFolderFormIds={currentFetchFolderFormIds}
          previousSelectedForms={currentSelectedForms}
          previousSelectedFormsData={currentSelectedFormsData}
          previousSelectedTasks={currentSelectedTasks}
          onClose={currentOnClose}
          onConfirmFormsSelection={currentOnConfirm}
        />
      )}
    </Layout>
  );
};

export default StatisticsPage;
